export const VAT_RATE = 0.19;

export const PLAN_TIERS = {
    INVOICE: 'INVOICE',
    ACCOUNTING: 'ACCOUNTING',
};

export const BILLING_CYCLES = {
    MONTHLY: 'MONTHLY',
    YEARLY: 'YEARLY',
};

export const PLAN_KEYS = {
    PENDING_PAYMENT: 'PENDING_PAYMENT',
    INVOICE_MONTHLY: 'INVOICE_MONTHLY',
    INVOICE_YEARLY: 'INVOICE_YEARLY',
    ACCOUNTING_MONTHLY: 'ACCOUNTING_MONTHLY',
    ACCOUNTING_YEARLY: 'ACCOUNTING_YEARLY',
    TEST: 'TEST',
};

const DEFAULT_PLAN = {
    tier: PLAN_TIERS.INVOICE,
    cycle: BILLING_CYCLES.MONTHLY,
};

const PLAN_KEY_MAP = {
    [PLAN_KEYS.INVOICE_MONTHLY]: { tier: PLAN_TIERS.INVOICE, cycle: BILLING_CYCLES.MONTHLY },
    [PLAN_KEYS.INVOICE_YEARLY]: { tier: PLAN_TIERS.INVOICE, cycle: BILLING_CYCLES.YEARLY },
    [PLAN_KEYS.ACCOUNTING_MONTHLY]: { tier: PLAN_TIERS.ACCOUNTING, cycle: BILLING_CYCLES.MONTHLY },
    [PLAN_KEYS.ACCOUNTING_YEARLY]: { tier: PLAN_TIERS.ACCOUNTING, cycle: BILLING_CYCLES.YEARLY },
    [PLAN_KEYS.TEST]: { tier: PLAN_TIERS.ACCOUNTING, cycle: BILLING_CYCLES.MONTHLY },
};

const NET_PRICES = {
    [PLAN_TIERS.INVOICE]: {
        [BILLING_CYCLES.MONTHLY]: 12.605,
        [BILLING_CYCLES.YEARLY]: 126.05,
    },
    [PLAN_TIERS.ACCOUNTING]: {
        [BILLING_CYCLES.MONTHLY]: 21.01,
        [BILLING_CYCLES.YEARLY]: 210.08,
    },
};

export const PLAN_DEFINITIONS = {
    [PLAN_TIERS.INVOICE]: {
        title: 'Rechnungen',
        subtitle: 'Alles für Angebote & Rechnungen',
        features: [
            'Rechnungen & Angebote (Nummernkreis, Rabatte)',
            'Kunden- & Artikelverwaltung',
            'Digitale Ablage ohne PDF-Export',
        ],
    },
    [PLAN_TIERS.ACCOUNTING]: {
        title: 'Buchhaltung',
        subtitle: 'Enthält das Rechnungsmodul plus:',
        features: [
            'Einnahmenverwaltung',
            'Ausgabenverwaltung mit OCR & Upload',
            'Steuerliche Auswertungen (UStVA, Jahreserklärung, EÜR)',
        ],
    },
    TEST: {
        title: 'Test-User',
        subtitle: 'Individueller Testzugang',
        features: [
            'Einnahmenverwaltung',
            'Ausgabenverwaltung mit OCR & Upload',
            'Steuerliche Auswertungen (UStVA, Jahreserklärung, EÜR)',
        ],
    }
};

export const PLAN_ORDER = [PLAN_TIERS.INVOICE, PLAN_TIERS.ACCOUNTING];

export function buildPlanKey(tier, cycle) {
    return `${tier}_${cycle}`;
}

export function resolvePlan(planKey) {
    if (!planKey) {
        return { ...DEFAULT_PLAN, valid: false };
    }
    const normalized = String(planKey).trim().toUpperCase();
    const match = PLAN_KEY_MAP[normalized];
    if (!match) {
        return { ...DEFAULT_PLAN, valid: false };
    }
    return { ...match, valid: true };
}

export function parsePlanKey(planKey) {
    const { tier, cycle } = resolvePlan(planKey);
    return { tier, cycle };
}

export function describePlan(planKey) {
    if (String(planKey || '').trim().toUpperCase() === PLAN_KEYS.PENDING_PAYMENT) {
        return 'Freischaltung ausstehend';
    }
    const resolved = resolvePlan(planKey);
    if (!resolved.valid) {
        return 'Unbekannter Tarif';
    }
    const tierLabel = resolved.tier === PLAN_TIERS.ACCOUNTING ? 'Buchhaltung' : 'Rechnungsmodul';
    const cycleLabel = resolved.cycle === BILLING_CYCLES.YEARLY ? 'jährlich' : 'monatlich';
    return `${tierLabel} (${cycleLabel})`;
}

export function getNetPriceFor(tier, cycle) {
    return NET_PRICES?.[tier]?.[cycle] ?? 0;
}

export function getNetPriceFromKey(planKey) {
    const { tier, cycle } = parsePlanKey(planKey);
    return getNetPriceFor(tier, cycle);
}

export function getDisplayPrice(planKey, includeVat) {
    const base = getNetPriceFromKey(planKey);
    const factor = includeVat ? 1 + VAT_RATE : 1;
    return Math.round(base * factor * 100) / 100;
}

export function hasAccountingAccess(planKey) {
    const { tier } = parsePlanKey(planKey);
    return tier === PLAN_TIERS.ACCOUNTING;
}

export function hasActiveSubscriptionAccess(user) {
    if (!user) {
        return false;
    }
    const planKey = String(user.paymentPlan || '').trim().toUpperCase();
    if (!planKey || planKey === PLAN_KEYS.PENDING_PAYMENT) {
        return false;
    }
    if (!user.paidUntil) {
        return false;
    }
    const paidUntil = new Date(user.paidUntil);
    if (Number.isNaN(paidUntil.getTime())) {
        return false;
    }
    paidUntil.setHours(23, 59, 59, 999);
    return paidUntil >= new Date();
}

export function formatCurrency(value) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value ?? 0);
}

export function getTierDefinition(tier) {
    return PLAN_DEFINITIONS[tier] || PLAN_DEFINITIONS[PLAN_TIERS.INVOICE];
}

export function getCycleLabel(cycle) {
    return cycle === BILLING_CYCLES.YEARLY ? 'jährlich' : 'monatlich';
}

export function getCycleSuffix(cycle) {
    return cycle === BILLING_CYCLES.YEARLY ? 'Jahr' : 'Monat';
}

export function monthlyPriceForTier(tier, includeVat) {
    const key = buildPlanKey(tier, BILLING_CYCLES.MONTHLY);
    return getDisplayPrice(key, includeVat);
}

export function yearlyPriceForTier(tier, includeVat) {
    const key = buildPlanKey(tier, BILLING_CYCLES.YEARLY);
    return getDisplayPrice(key, includeVat);
}

